import { computed, ref } from 'vue';
import { useOrderStore } from '@/Stores/Web/orderStore';
import { paymentDataSources, paymentCollectionTypes, projectTypes } from '@/Models';
import { buildAvailablePaymentMethods, buildPaymentMethodDisclaimer } from '@/Components/Payments/PaymentUtils';
import { sectionTypes, redirectOrExternalFlowPaymentMethods } from '@/Components/Payments/Adyen/AdyenPaymentUtility';
import GooglePayService from '@/Services/Payments/GooglePayService';
import ApplePayService from '@/Services/Payments/ApplePayService';
import Resources from '@/Resources';

export function useAdyenPaymentMethods({
    adyenPaymentContext,
    availablePaymentMethods,
    paymentMethodsReason,
    projectType,
    isInTestMode,
    isExpressCrowdfunding,
}) {
    // TODO: orderStore manages available payment methods, disabled payment methods and selected payment method
    // which is not order responsibility. Consider extracting to a dedicated payment methods store.
    const orderStore = useOrderStore();
    const disabledPaymentMethods = ref([]);

    const googlePayService = new GooglePayService(adyenPaymentContext);
    const applePayService = new ApplePayService(adyenPaymentContext);

    const selectedPaymentMethod = computed(() => orderStore.selectedPaymentMethod);

    function setSelectedPaymentMethod(val) {
        orderStore.setSelectedPaymentMethod(val);
    }

    const filteredAvailablePaymentMethods = computed(() => {
        if (availablePaymentMethods.value) {
            return buildAvailablePaymentMethods(availablePaymentMethods.value, disabledPaymentMethods.value);
        }

        return null;
    });

    const paymentMethodsList = computed(() => {
        const result = availablePaymentMethods.value
            ? filteredAvailablePaymentMethods.value
            : orderStore.availablePaymentMethods;

        return result.map((pm) => {
            const isSelected = selectedPaymentMethod.value === pm.paymentDataSource;
            const form = isInTestMode.value ? null : pm.form;

            const item = {
                ...pm,
                isSelected,
                form,
            };

            if (!availablePaymentMethods.value) {
                item.paymentDisclaimer = buildDisclaimer(pm.paymentCollectionType, pm.paymentDataSource);
            }

            return item;
        });
    });

    const groupedPaymentMethods = computed(() => {
        const recentPaymentMethodTypes = [paymentDataSources.currentCard, paymentDataSources.storedCard];

        const recentPaymentMethodItems = paymentMethodsList.value
            .filter(pm => recentPaymentMethodTypes.includes(pm.paymentDataSource));

        const otherPaymentMethodItems = paymentMethodsList.value
            .filter(pm => !recentPaymentMethodTypes.includes(pm.paymentDataSource))
            .map(mapPaymentMethodLabel);

        const otherPrimaryPaymentMethods = otherPaymentMethodItems
            .filter(pm => pm.isSuggested);

        const otherSecondaryPaymentMethods = otherPaymentMethodItems
            .filter(pm => !pm.isSuggested);

        const isTitleVisible = recentPaymentMethodItems.length > 0;

        const sections = [
            {
                sectionType: sectionTypes.recent,
                title: Resources.Checkout.RecentPaymentMethodsLabel,
                isTitleVisible,
                paymentMethods: recentPaymentMethodItems,
            },
            {
                sectionType: sectionTypes.suggested,
                title: Resources.Checkout.OtherPaymentMethodsLabel,
                isTitleVisible,
                paymentMethods: otherPrimaryPaymentMethods,
            },
        ];

        if (otherSecondaryPaymentMethods.length > 0) {
            sections.push({
                sectionType: sectionTypes.other,
                title: Resources.Common.ShowLessLabel,
                paymentMethods: otherSecondaryPaymentMethods,
            });
        }

        return sections;
    });

    const defaultPaymentMethod = computed(() => {
        const { currentCard, storedCard } = paymentDataSources;
        const methods = orderStore.availablePaymentMethods;
        if (methods.length === 1) {
            return methods[0];
        }

        const currentCardPaymentMethod = methods.find(c => c.paymentDataSource === currentCard);
        const storedCardPaymentMethod = methods.find(c => c.paymentDataSource === storedCard);

        return currentCardPaymentMethod ?? storedCardPaymentMethod;
    });

    // TODO: isCrowdfunding, buildDisclaimer and mapPaymentMethodLabel use project type knowledge
    // to decide on disclaimers and labels. This should come from backend as part of payment method data
    // (e.g. paymentMethod.disclaimer, paymentMethod.label) instead of being derived from project type here.
    function isCrowdfunding() {
        return projectType.value === projectTypes.crowdfunding;
    }

    function buildDisclaimer(paymentCollectionType, paymentDataSource) {
        if (isCrowdfunding()) {
            return buildPaymentMethodDisclaimer(paymentCollectionType, paymentDataSource, isExpressCrowdfunding.value);
        }

        return null;
    }

    function mapPaymentMethodLabel(paymentMethod) {
        const isInstantPayment = paymentMethod.paymentCollectionType === paymentCollectionTypes.instant;

        if (isInstantPayment && isCrowdfunding() && !isExpressCrowdfunding.value) {
            return {
                ...paymentMethod,
                label: Resources.Checkout.PaymentCollectionTypeInstantLabel,
                labelTheme: 'primary',
            };
        }
        return paymentMethod;
    }

    function getSelectedPaymentMethodSectionType() {
        return groupedPaymentMethods.value
            .find(c => c.paymentMethods.some(p => p.paymentDataSource === selectedPaymentMethod.value))
            .sectionType;
    }

    function selectPaymentMethod(paymentDataSource) {
        if (selectedPaymentMethod.value === paymentDataSource) return;
        setSelectedPaymentMethod(paymentDataSource);
    }

    async function initPaymentServices() {
        await Promise.all([
            googlePayService.initAsync(),
            applePayService.initAsync(),
        ]);

        const isGooglePayAvailable = await googlePayService.isAvailableAsync();
        const isApplePayAvailable = await applePayService.isAvailableAsync();

        if (!isGooglePayAvailable) {
            disabledPaymentMethods.value.push(paymentDataSources.googlePay);
        }
        if (!isApplePayAvailable) {
            disabledPaymentMethods.value.push(paymentDataSources.applePay);
        }

        orderStore.setDisabledPaymentMethods(disabledPaymentMethods.value);
    }

    async function fetchAvailablePaymentMethods(projectId) {
        await orderStore.getAvailablePaymentMethods(orderStore.locationIsoCode, paymentMethodsReason.value, projectId);
        orderStore.setDisabledPaymentMethods(disabledPaymentMethods.value);
    }

    function isInstantPayment(paymentDataSource) {
        const paymentMethod = paymentMethodsList.value
            .find(i => i.paymentDataSource === paymentDataSource);

        return paymentMethod?.paymentCollectionType === paymentCollectionTypes.instant;
    }

    function createApplePaySession(currencyShortName, amount) {
        applePayService.createSession(currencyShortName, amount);
    }

    function invokeApplePay(callbacks) {
        applePayService.invoke(callbacks);
    }

    function invokeGooglePay(currencyShortName, amount) {
        return googlePayService.invokeAsync(currencyShortName, amount);
    }

    async function processPayment(paymentSubmission, { currencyShortName, payableAmount, paymentFormData, applePayCallbacks }) {
        const selected = selectedPaymentMethod.value;

        if (selected === paymentDataSources.newCard) {
            return paymentSubmission.buildCardPayment(paymentFormData);
        }

        if (!isInTestMode.value && selected === paymentDataSources.googlePay) {
            paymentSubmission.storeCreditCard.value = false;
            const paymentData = await invokeGooglePay(currencyShortName, payableAmount);
            const { token } = paymentData.paymentMethodData.tokenizationData;
            return paymentSubmission.buildTokenPayment(token, paymentData.email, paymentDataSources.googlePay);
        }

        if (!isInTestMode.value && selected === paymentDataSources.applePay) {
            paymentSubmission.storeCreditCard.value = false;
            invokeApplePay(applePayCallbacks);
            return null;
        }

        if (selected === paymentDataSources.storedCard || selected === paymentDataSources.currentCard) {
            return paymentSubmission.buildSystemPayment(selected);
        }

        if (redirectOrExternalFlowPaymentMethods.includes(selected)
            || isInTestMode.value && (selected === paymentDataSources.googlePay
                || selected === paymentDataSources.applePay)) {
            return paymentSubmission.buildAdditionalPayment(selected, paymentFormData);
        }
    }

    return {
        selectedPaymentMethod,
        setSelectedPaymentMethod,
        groupedPaymentMethods,
        paymentMethodsList,
        defaultPaymentMethod,
        getSelectedPaymentMethodSectionType,
        selectPaymentMethod,
        initPaymentServices,
        fetchAvailablePaymentMethods,
        isInstantPayment,
        processPayment,
        createApplePaySession,
    };
}
